/**
 * Play-ledger GBUX transfer API (user → user by username).
 * Server D1 only — requires Phantom/injected wallet message signature.
 */
import { authHeaders } from "@/lib/grudge-auth";
import { signMessageBase58, type ConnectedSolanaWallet } from "@/lib/solana-wallets";

export interface LookupUserResult {
  success: boolean;
  username?: string;
  walletHint?: string;
  matches?: number;
  error?: string;
}

export interface TransferResult {
  success: boolean;
  status?: string;
  amount?: number;
  message?: string;
  error?: string;
  playGbux?: number;
  from?: { wallet: string; playGbux: number };
  to?: { username: string; walletHint: string; playGbux: number };
}

function walletHeaders(wallet: string, extra?: HeadersInit): Headers {
  const h = authHeaders(extra);
  h.set("X-Wallet-Address", wallet);
  return h;
}

export async function lookupPlayUser(username: string): Promise<LookupUserResult> {
  const u = username.trim().replace(/^@+/, "");
  if (u.length < 2) return { success: false, error: "Enter a username" };
  const r = await fetch(
    `/api/play/lookup-user?username=${encodeURIComponent(u)}`,
  );
  const j = (await r.json()) as LookupUserResult;
  if (!r.ok) return { success: false, error: j.error || "User not found" };
  return j;
}

export async function fetchTransferChallenge(opts: {
  fromWallet: string;
  toUsername: string;
  amount: number;
}): Promise<{ message: string; nonce: string; issuedAt: string }> {
  const q = new URLSearchParams({
    wallet: opts.fromWallet,
    toUsername: opts.toUsername.replace(/^@+/, ""),
    amount: String(Math.floor(opts.amount)),
  });
  const r = await fetch(`/api/play/transfer-challenge?${q}`, {
    headers: walletHeaders(opts.fromWallet),
  });
  const j = (await r.json()) as {
    success?: boolean;
    message?: string;
    nonce?: string;
    issuedAt?: string;
    error?: string;
  };
  if (!r.ok || !j.message) {
    throw new Error(j.error || "Could not build transfer challenge");
  }
  return {
    message: j.message,
    nonce: j.nonce || "",
    issuedAt: j.issuedAt || "",
  };
}

/**
 * POST play transfer with pre-signed challenge (mid-app Sign UI).
 */
export async function transferPlayGbuxByUsername(opts: {
  wallet: ConnectedSolanaWallet | { publicKey: string };
  toUsername: string;
  amount: number;
  note?: string;
  /** From dApp Sign modal — preferred */
  signed?: { message: string; signature: string };
}): Promise<TransferResult> {
  const fromWallet = opts.wallet.publicKey;
  const toUsername = opts.toUsername.trim().replace(/^@+/, "");
  const amount = Math.floor(Number(opts.amount));
  if (!fromWallet) throw new Error("Wallet not connected");
  if (toUsername.length < 2) throw new Error("Recipient username required");
  if (amount < 1) throw new Error("Amount must be at least 1 GBUX");

  let message = opts.signed?.message;
  let signature = opts.signed?.signature;
  if (!message || !signature) {
    const challenge = await fetchTransferChallenge({
      fromWallet,
      toUsername,
      amount,
    });
    message = challenge.message;
    const connected = opts.wallet as ConnectedSolanaWallet;
    if (!connected.provider?.signMessage) {
      throw new Error("Use Sign UI or reconnect a signing wallet");
    }
    signature = await signMessageBase58(connected, challenge.message);
  }
  const idempotencyKey =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `p2p-${Date.now()}-${amount}`;

  const r = await fetch("/api/play/transfer", {
    method: "POST",
    headers: walletHeaders(fromWallet, {
      "Idempotency-Key": idempotencyKey,
    }),
    body: JSON.stringify({
      fromWallet,
      toUsername,
      amount,
      note: opts.note?.trim() || undefined,
      message,
      signature,
      idempotencyKey,
    }),
  });

  const j = (await r.json()) as TransferResult & { error?: string };
  if (!r.ok) {
    return {
      success: false,
      error: j.error || j.message || `Transfer failed (${r.status})`,
      playGbux: j.from?.playGbux ?? j.playGbux,
    };
  }
  return {
    success: true,
    status: j.status,
    amount: j.amount ?? amount,
    message: j.message,
    from: j.from,
    to: j.to,
    playGbux: j.from?.playGbux,
  };
}
